import React, { useState, useEffect } from 'react'
import { makeStyles, Typography, Grid, Hidden, Link } from '@material-ui/core'
import { StakingPlatform, StakingPlatformTVL } from '../../../../../types/stakingPlatform'
import EthStakingCard from './EthStakingCard'
import { getEthPrice } from '../../../../services/stakingPlatforms'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '20px 20px',
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
      padding: '20px 120px 0',
    },
  },
  header: {
    margin: '1rem 0',
    letterSpacing: '-0.02em',
    fontSize: '48px',
    maxWidth: '40%',
    [theme.breakpoints.down('sm')]: {
      fontSize: '48px',
    },
  },
  chip: {
    background: 'rgba(11, 11, 11, 0.8)',
    color: 'white',
  },
  link: {
    color: 'white',
    fontSize: '.8rem',
    padding: '.4rem 0',
    borderBottom: '1px solid white',
  },
  contentSection: {
    margin: '1rem 0',
    '& p': {
      color: 'white',
    },
  },
  contentCard: {
    padding: '1rem',
    background: 'rgba(255, 255, 255, 0.06)',
    borderRadius: '.4rem',
  },
  cardBody: {
    fontSize: '1rem',
  },
  cardHeader: {
    margin: '0 0 1rem 0',
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  navContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  linkContainer: {
    display: 'inline-flex',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row',
      margin: '1rem 0',
    },
    '& > a': {
      fontSize: '.84rem',
      color: 'rgba(43, 24, 65, 1)',
      padding: '.4rem 1rem',
      cursor: 'pointer',
      borderRadius: '.8rem',
      margin: '0 .4rem',
      fontWeight: '600',
    },
    '& > a.active': {
      background: 'rgba(0, 0, 0, 0.04)',
      color: 'black',
    },
  },
  selectContainer: {
    minWidth: '11rem',
  },
  cryptoContainer: {
    display: 'flex',
    minWidth: '50%',
  },
  cryptoChip: {
    margin: '0 1rem 0 0',
    padding: '.6rem 1rem',
    borderRadius: '2rem',
    display: 'inline-flex',
    alignItems: 'center',
    color: 'rgba(0,0,0,0.6)',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '.8rem',
    '& > span': {
      height: '20px',
      width: '20px',
      borderRadius: '50%',
      background: 'rgba(196, 196, 196, 1)',
      display: 'inline-block',
      marginRight: '.4rem',
    },
    '&.active': {
      background: 'rgba(0, 0, 0, 0.04)',
      color: 'rgba(0,0,0,0.9)',
    },
  },
  tableHeader: {
    padding: '0rem 2rem',
    margin: '2rem 0 0 0',
    '&>div': {
      padding: '0 !important',
    },
    '& p': {
      fontSize: '.9rem',
      padding: '1rem 0',
    },
  },
  error: {
    padding: '50px',
    margin: '50px',
  },
  exchangeContainer: {
    marginBottom: '0rem',
  },
  exchangesListContainer: {
    padding: '10px 0px',
    [theme.breakpoints.up('md')]: {
      padding: '0px 0px',
    },
    [theme.breakpoints.up('xl')]: {
      margin: 'auto',
    },
  },
  cryptostats: {
    display: 'flex',
    gap: '1rem',
    margin: '0 0 1rem 0',
    alignItems: 'center',
    '& > p': {
      fontSize: '.8rem',
    },
  },
}))

interface link {
  label: string
  url: string
}

const navLinks = [
  {
    label: 'Staking Pool',
    url: 'staking-pool',
  },
  {
    label: 'Solo Staking',
    url: 'solo-staking',
  },
] as link[]

interface Props {
  platforms: StakingPlatform[]
  title?: string
  tvls: StakingPlatformTVL[]
}

function StakingPlatformItem({
  platform,
  number,
  tvl,
  showTVL,
  ethPrice,
}: {
  platform: StakingPlatform
  number: number
  tvl?: number | undefined
  showTVL?: boolean
  ethPrice?: number
}) {
  const classes = useStyles({})
  return (
    <div className={classes.exchangeContainer}>
      <EthStakingCard
        platform={platform}
        number={number}
        tvl={tvl}
        showTvl={showTVL}
        ethPrice={ethPrice}
      />
    </div>
  )
}

export default function ExchangesTable({ platforms, title, tvls }: Props): JSX.Element {
  const classes = useStyles({})

  const [activeLink, setActiveLink] = useState(navLinks[0])

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.split('#')[1]
      const activeLink = navLinks.find((link) => link.url === hash) || navLinks[0]
      setActiveLink(activeLink)
    }
    if (!window.onhashchange) {
      window.onhashchange = handleHashChange
    }
    handleHashChange()
  })

  const filterPlatforms = () => {
    switch (activeLink.url) {
      case 'staking-pool':
        return platforms.filter((obj) => obj.staking_type === 'Pool')
        break
      case 'solo-staking':
        return platforms.filter((obj) => obj.staking_type === 'Solo')
        break
      default:
        return platforms
    }
  }

  const sortByTVL = (platforms: StakingPlatform[]) => {
    return platforms.sort((a, b) => {
      const aTVL = getTVL(a) || 0
      const bTVL = getTVL(b) || 0

      return bTVL - aTVL
    })
  }

  const getTVL = (platform: StakingPlatform) => {
    return tvls.find((tvl) => tvl.id === platform.tvl_api_id)?.results?.totalStakedETH
  }

  const [ethPrice, setEthPrice] = React.useState<number | undefined>(undefined)

  React.useEffect(() => {
    getEthPrice().then((price) => {
      setEthPrice(price)
    })
  })

  const filteredPlatforms = filterPlatforms()

  return (
    <div className={classes.root}>
      <nav className={classes.navContainer}>
        <Typography variant="h3" className={classes.header}>
          {title || 'Top 10 crypto exchanges'}
        </Typography>
      </nav>
      <div className={classes.cryptostats}>
        <Typography variant="body2">Powered by </Typography>
        <img src="/cryptostats-logo.svg" alt="CryptoStats" />
        <Typography variant="body2"> Cryptostats </Typography>
      </div>
      <div className={classes.linkContainer}>
        {navLinks.map((link, index) => (
          <Link
            href={`#${link.url}`}
            key={index}
            underline="none"
            className={activeLink.url === link.url ? 'active' : ''}
            rel="prefetch"
          >
            {link.label}
          </Link>
        ))}
      </div>

      <div>
        <Hidden smDown>
          <Grid container alignItems="center" spacing={6} className={classes.tableHeader}>
            <Grid item xs={1}></Grid>
            <Grid item xs={4} md={2}>
              <Typography variant="body2">Platform</Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography align="center" style={{ paddingRight: '3rem' }} variant="body2">
                Fee
              </Typography>
            </Grid>
            <Grid item xs={2} md={1}>
              <Typography variant="body2">DeFi</Typography>
            </Grid>
            <Grid item xs={2} md={2}>
              <Typography variant="body2">Min Stake</Typography>
            </Grid>
            <Hidden smDown>
              {activeLink.url === 'staking-pool' && (
                <Grid item xs={2} md={2}>
                  <Typography variant="body2">TVL</Typography>
                </Grid>
              )}

              <Grid item xs={4}></Grid>
            </Hidden>
          </Grid>
        </Hidden>
      </div>

      <div className={classes.exchangesListContainer}>
        {sortByTVL(filteredPlatforms).map((obj: StakingPlatform, index) => (
          <StakingPlatformItem
            tvl={getTVL(obj)}
            platform={obj}
            key={index}
            number={index + 1}
            showTVL={activeLink.url === 'staking-pool'}
            ethPrice={ethPrice}
          />
        ))}
      </div>
    </div>
  )
}
